import type {
  CreateTravelerDto,
  TravelerResponseDto,
  UpdateTravelerDto,
} from '@bandido/shared/dtos';

import type { TravelerFormValues } from './types';

export const emptyTravelerFormValues = (): TravelerFormValues => ({
  title: '',
  firstName: '',
  lastName: '',
  birthDate: '',
  email: '',
  phone: '',
  nationality: '',
  countryOfResidence: '',
  documents: [],
});

export const travelerToFormValues = (
  traveler?: TravelerResponseDto,
): TravelerFormValues =>
  traveler
    ? {
      title: traveler.title ?? '',
      firstName: traveler.firstName,
      lastName: traveler.lastName,
      birthDate: traveler.birthDate ?? '',
      email: traveler.email ?? '',
      phone: traveler.phone ?? '',
      nationality: traveler.nationality ?? '',
      countryOfResidence: traveler.countryOfResidence ?? '',
      documents: traveler.documents.map((document) => ({
        id: document.id,
        type: document.type,
        issuingCountry: document.issuingCountry ?? '',
        number: document.number,
        expiresOn: document.expiresOn ?? '',
      })),
    }
    : emptyTravelerFormValues();

const nullable = (value: string): string | null => value.trim() || null;

export const travelerFormPayload = (
  values: TravelerFormValues,
  editing: boolean,
  initialValues?: TravelerFormValues,
): CreateTravelerDto | UpdateTravelerDto => {
  const payload = {
    title: nullable(values.title),
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    birthDate: nullable(values.birthDate),
    email: nullable(values.email),
    phone: nullable(values.phone),
    nationality: nullable(values.nationality)?.toUpperCase() ?? null,
    countryOfResidence:
      nullable(values.countryOfResidence)?.toUpperCase() ?? null,
    documents: values.documents.map((document) => ({
      type: document.type.trim().toLowerCase(),
      issuingCountry:
        nullable(document.issuingCountry)?.toUpperCase() ?? undefined,
      number: document.number.trim(),
      expiresOn: nullable(document.expiresOn) ?? undefined,
    })),
  };

  if (editing) {
    if (!initialValues) return payload;

    const initialPayload = travelerFormPayload(
      initialValues,
      true,
    ) as UpdateTravelerDto;
    const update: UpdateTravelerDto = {};

    if (payload.title !== initialPayload.title) update.title = payload.title;
    if (payload.firstName !== initialPayload.firstName) {
      update.firstName = payload.firstName;
    }
    if (payload.lastName !== initialPayload.lastName) {
      update.lastName = payload.lastName;
    }
    if (payload.birthDate !== initialPayload.birthDate) {
      update.birthDate = payload.birthDate;
    }
    if (payload.email !== initialPayload.email) update.email = payload.email;
    if (payload.phone !== initialPayload.phone) update.phone = payload.phone;
    if (payload.nationality !== initialPayload.nationality) {
      update.nationality = payload.nationality;
    }
    if (payload.countryOfResidence !== initialPayload.countryOfResidence) {
      update.countryOfResidence = payload.countryOfResidence;
    }
    if (
      JSON.stringify(payload.documents) !==
      JSON.stringify(initialPayload.documents)
    ) {
      update.documents = payload.documents;
    }

    return update;
  }

  return {
    ...payload,
    title: payload.title ?? undefined,
    birthDate: payload.birthDate ?? undefined,
    email: payload.email ?? undefined,
    phone: payload.phone ?? undefined,
    nationality: payload.nationality ?? undefined,
    countryOfResidence: payload.countryOfResidence ?? undefined,
  };
};

export const hasTravelerFormChanges = (
  values: TravelerFormValues,
  initialValues: TravelerFormValues,
) => Object.keys(travelerFormPayload(values, true, initialValues)).length > 0;

export const maskDocumentNumber = (value: string): string => {
  const compact = value.replace(/\s/g, '');
  if (!compact) return '';
  if (compact.length <= 4) return '•'.repeat(Math.max(4, compact.length));
  const visible = compact.slice(-4);
  const hiddenCount = Math.max(4, compact.length - visible.length);
  return `${'•'.repeat(hiddenCount)} ${visible}`;
};

export const travelerDisplayName = (
  traveler: Pick<TravelerResponseDto, 'firstName' | 'lastName'>,
): string => `${traveler.firstName} ${traveler.lastName}`.trim();
